// ============================================================================
// Unit conversion
// ============================================================================

const VOLUME_TO_TSP = {
  tsp: 1,
  tbsp: 3,
  'fl oz': 6,
  cup: 48,
};

const WEIGHT_TO_OZ = {
  oz: 1,
  lb: 16,
};

const VOLUME_UNITS = new Set(Object.keys(VOLUME_TO_TSP));
const WEIGHT_UNITS = new Set(Object.keys(WEIGHT_TO_OZ));

function unitFamily(unit) {
  const u = unit.toLowerCase().trim();
  if (VOLUME_UNITS.has(u)) return 'volume';
  if (WEIGHT_UNITS.has(u)) return 'weight';
  return null;
}

/** Convert an amount+unit to the base unit of its family (tsp or oz). */
export function convertToBase(amount, unit) {
  const u = unit.toLowerCase().trim();
  if (VOLUME_UNITS.has(u)) return { amount: amount * VOLUME_TO_TSP[u], baseUnit: 'tsp' };
  if (WEIGHT_UNITS.has(u)) return { amount: amount * WEIGHT_TO_OZ[u], baseUnit: 'oz' };
  return { amount, baseUnit: u };
}

/**
 * Pick the most human-readable unit for a base-unit amount.
 * E.g. 48 tsp → 1 cup, 6 tsp → 2 tbsp, 0.5 tsp → 0.5 tsp.
 */
export function convertToDisplay(amount, baseUnit) {
  if (baseUnit === 'tsp') {
    // Prefer cup ≥ 0.25 cup, then tbsp ≥ 1, else tsp
    const cups = amount / VOLUME_TO_TSP.cup;
    if (cups >= 0.25 && Number.isFinite(cups)) {
      const rounded = Math.round(cups * 100) / 100;
      return { amount: rounded, unit: 'cup' };
    }
    const tbsp = amount / VOLUME_TO_TSP.tbsp;
    if (tbsp >= 1 && Number.isFinite(tbsp)) {
      const rounded = Math.round(tbsp * 100) / 100;
      return { amount: rounded, unit: 'tbsp' };
    }
    return { amount: Math.round(amount * 100) / 100, unit: 'tsp' };
  }

  if (baseUnit === 'oz') {
    const lbs = amount / WEIGHT_TO_OZ.lb;
    if (lbs >= 1 && Number.isFinite(lbs)) {
      const rounded = Math.round(lbs * 100) / 100;
      return { amount: rounded, unit: 'lb' };
    }
    return { amount: Math.round(amount * 100) / 100, unit: 'oz' };
  }

  return { amount: Math.round(amount * 100) / 100, unit: baseUnit };
}

/** Convert between any two units in the same family. */
export function convertUnits(amount, fromUnit, toUnit) {
  const from = fromUnit.toLowerCase().trim();
  const to = toUnit.toLowerCase().trim();
  if (from === to) return amount;

  const fromFamily = unitFamily(from);
  const toFamily = unitFamily(to);
  if (!fromFamily || fromFamily !== toFamily) return amount; // incompatible, return as-is

  const { amount: base } = convertToBase(amount, from);
  if (fromFamily === 'volume') return base / VOLUME_TO_TSP[to];
  if (fromFamily === 'weight') return base / WEIGHT_TO_OZ[to];
  return amount;
}

// ============================================================================
// Item name normalization
// ============================================================================

const PREP_MODIFIERS = new Set([
  'diced', 'sliced', 'chopped', 'minced', 'shredded', 'grated',
  'fresh', 'large', 'small', 'thinly', 'finely', 'frozen', 'dried',
]);

const ALIASES = {
  'broccoli florets': 'broccoli',
  'broccoli floret': 'broccoli',
  'chicken breast': 'chicken breasts',
  'bell pepper': 'bell peppers',
  'green onion': 'green onions',
  'scallion': 'green onions',
  'scallions': 'green onions',
};

/**
 * Produce a normalized key for matching/aggregation.
 * Lowercases, strips prep modifiers, applies aliases, and normalizes plurals.
 */
export function normalizeItemName(name) {
  let n = name.toLowerCase().trim();

  // Strip prep modifiers
  const words = n.split(/\s+/).filter(w => !PREP_MODIFIERS.has(w));
  n = words.join(' ');

  // Apply alias map
  if (ALIASES[n]) n = ALIASES[n];

  // Simple plural normalization for the key only:
  // "tomatoes" → "tomato", "potatoes" → "potato"
  // But don't strip 's' from words like "hummus", "couscous" (ending in double-s)
  if (n.endsWith('oes') && !n.endsWith('shoes')) {
    n = n.slice(0, -2); // tomatoes → tomato
  } else if (n.endsWith('ies')) {
    n = n.slice(0, -3) + 'y'; // berries → berry
  } else if (n.endsWith('es') && !n.endsWith('ss')) {
    n = n.slice(0, -2); // peaches → peach
  } else if (n.endsWith('s') && !n.endsWith('ss') && !n.endsWith('us')) {
    n = n.slice(0, -1); // carrots → carrot
  }

  return n;
}

// ============================================================================
// Aggregation
// ============================================================================

/**
 * Aggregate arrays of ingredients into a merged shopping list.
 *
 * @param {Array<Array<{item: string, amount?: number, unit?: string, section?: string}>>} ingredientArrays
 * @returns {Array<{item: string, amount: number, unit: string, section: string}>}
 */
export function aggregateIngredients(ingredientArrays) {
  // Map from normalizedKey → { item (display), amount (in base unit), baseUnit, section }
  const agg = {};

  for (const ingredients of ingredientArrays) {
    for (const ing of ingredients) {
      const key = normalizeItemName(ing.item);
      const amount = ing.amount ?? 0;
      const unit = (ing.unit || '').toLowerCase().trim();
      const section = ing.section || 'pantry';
      const hasQuantity = amount > 0;

      if (!agg[key]) {
        // First time seeing this item
        if (hasQuantity) {
          const base = convertToBase(amount, unit);
          agg[key] = { item: ing.item, amount: base.amount, baseUnit: base.baseUnit, section };
        } else {
          agg[key] = { item: ing.item, amount: 0, baseUnit: '', section };
        }
        continue;
      }

      const entry = agg[key];

      if (!hasQuantity) continue; // nothing to add

      const base = convertToBase(amount, unit);

      if (entry.amount === 0 && !entry.baseUnit) {
        // Previous was no-quantity; upgrade to this quantity
        entry.amount = base.amount;
        entry.baseUnit = base.baseUnit;
        continue;
      }

      if (entry.baseUnit === base.baseUnit) {
        // Same family — add directly
        entry.amount += base.amount;
      } else {
        // Different families (e.g., volume vs weight) — can't merge, just add raw
        // Fall back: keep existing and append amount in original unit
        entry.amount += amount;
        // Keep existing baseUnit — imperfect but avoids data loss
      }
    }
  }

  // Convert base amounts back to display-friendly units
  return Object.values(agg).map(entry => {
    if (entry.amount === 0) {
      return { item: entry.item, amount: 0, unit: '', section: entry.section };
    }
    const display = convertToDisplay(entry.amount, entry.baseUnit);
    return { item: entry.item, amount: display.amount, unit: display.unit, section: entry.section };
  });
}
